import { AFFILIATE_SESSION_KEY } from "~/lib/affiliate/config";

function readSession(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeSession(key: string, value: string): void {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        // Storage blocked: nothing else depends on it.
    }
}

/** The partner for this tab, for analytics only. The httpOnly cookie is the source of truth. */
export const getSessionAffiliateSlug = () => readSession(AFFILIATE_SESSION_KEY.SLUG);

export const setSessionAffiliateSlug = (slug: string) => writeSession(AFFILIATE_SESSION_KEY.SLUG, slug);

function removeSession(key: string): void {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // Storage blocked: nothing was written either.
    }
}

function readSessionList(key: string): string[] {
    try {
        const parsed: unknown = JSON.parse(readSession(key) ?? "[]");

        return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
    } catch {
        return [];
    }
}

/** False when the list already holds the entry. */
function addToSessionList(key: string, entry: string): boolean {
    const entries = readSessionList(key);

    if (entries.includes(entry)) {
        return false;
    }

    writeSession(key, JSON.stringify([...entries, entry]));

    return true;
}

const normalizeCode = (code: string) => code.toUpperCase();

/** Every code removed in checkout this tab session, so checkout doesn't re-apply it. */
export function addSessionRemovedCoupon(code: string): void {
    addToSessionList(AFFILIATE_SESSION_KEY.REMOVED_COUPONS, normalizeCode(code));
}

export const isSessionRemovedCoupon = (code: string) =>
    readSessionList(AFFILIATE_SESSION_KEY.REMOVED_COUPONS).includes(normalizeCode(code));

/**
 * Records a partner coupon event for this checkout, so its next page doesn't
 * send it again. False when it was already sent.
 */
export const claimSessionCouponReport = (eventName: string, code: string) =>
    addToSessionList(AFFILIATE_SESSION_KEY.REPORTED_COUPONS, `${eventName}:${normalizeCode(code)}`);

/** Ends the checkout's partner coupon reports, so the next checkout in this tab reports its own. */
export const clearSessionCouponReports = () => removeSession(AFFILIATE_SESSION_KEY.REPORTED_COUPONS);
